import React, { useState, useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";

const PRICE_COLOR = 'green';
const HIGHLIGHT_COLOR = 'rgba(255, 0, 0, 0.7)';

// Vol trend gradient: blue (negative/bullish) → gray (0) → red (positive/bearish)
const getVolTrendColor = (value) => {
    if (value === null || value === undefined) return 'rgb(150, 150, 150)';
    const clamped = Math.max(-1, Math.min(1, value));
    const t = (clamped + 1) / 2;
    let r, g, b;
    if (t < 0.5) {
        const s = t * 2;
        r = Math.round(100 + 50 * s);
        g = Math.round(140 + 10 * s);
        b = Math.round(200 - 50 * s);
    } else {
        const s = (t - 0.5) * 2;
        r = Math.round(150 + 50 * s);
        g = Math.round(150 - 50 * s);
        b = Math.round(150 - 50 * s);
    }
    return `rgb(${r}, ${g}, ${b})`;
};

const VolTrendTimeSeriesChart = () => {
    const [data, setData] = useState([]);
    const [timestamp, setTimestamp] = useState("");
    const [loading, setLoading] = useState(true);
    const chartRef = useRef(null);

    const API_URL = process.env.REACT_APP_API_URL || "";

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${API_URL}/data/volatility`);
                if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
                const { data: jsonData, timestamp: fileTimestamp } = await response.json();
                setData(jsonData);
                setTimestamp(fileTimestamp);
            } catch (error) {
                console.error("Error fetching volatility data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [API_URL]);

    if (loading) return <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>;
    if (data.length === 0) return <div style={{ textAlign: 'center', padding: '20px' }}>No data available</div>;

    // Get latest vol_trend value
    const latestRow = data[data.length - 1];
    const latestVolTrend = latestRow?.vol_trend;
    const volTrendThreshold = 0.03;
    const vtLower = latestVolTrend != null ? latestVolTrend - volTrendThreshold : null;
    const vtUpper = latestVolTrend != null ? latestVolTrend + volTrendThreshold : null;

    // Find indices where vol_trend aligns with current (excluding latest)
    const similarIndices = new Set();
    for (let i = 0; i < data.length - 1; i++) {
        const vt = data[i].vol_trend;
        if (vtLower != null && vt !== null && vt >= vtLower && vt <= vtUpper) {
            similarIndices.add(i);
        }
    }

    const closeData = data.map((d, i) => ({ x: i, y: d.close }));
    const volTrendData = data.map((d, i) => ({ x: i, y: d.vol_trend }));
    const highlightData = data.map((d, i) =>
        similarIndices.has(i) ? { x: i, y: d.close } : { x: i, y: null }
    );
    const zeroLineData = [{ x: 0, y: 0 }, { x: data.length - 1, y: 0 }];
    const labels = data.map(d => d.date);

    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    const twoYearsAgoStr = twoYearsAgo.toISOString().split('T')[0];
    const startIdx = data.findIndex(d => d.date >= twoYearsAgoStr);
    const initialMinIdx = startIdx >= 0 ? startIdx : 0;

    const chartData = {
        labels,
        datasets: [
            {
                label: "S&P 500 Close",
                data: closeData,
                borderColor: PRICE_COLOR,
                backgroundColor: PRICE_COLOR,
                borderWidth: 1.5,
                tension: 0,
                fill: false,
                yAxisID: "y",
                pointRadius: 0,
                pointHitRadius: 0,
                order: 3,
            },
            {
                label: "Similar Dates",
                data: highlightData,
                borderColor: HIGHLIGHT_COLOR,
                backgroundColor: HIGHLIGHT_COLOR,
                borderWidth: 0,
                fill: false,
                yAxisID: "y",
                pointRadius: 4,
                pointStyle: 'circle',
                showLine: false,
                spanGaps: false,
                order: 1,
            },
            {
                label: "Vol Premium Momentum",
                data: volTrendData,
                borderColor: 'rgb(150, 150, 150)',
                backgroundColor: 'rgb(150, 150, 150)',
                borderWidth: 2,
                tension: 0,
                fill: false,
                yAxisID: "y1",
                pointRadius: 0,
                pointHitRadius: 0,
                order: 1,
                segment: {
                    borderColor: (ctx) => getVolTrendColor(ctx.p1.parsed.y)
                }
            },
            {
                label: "Zero Line",
                data: zeroLineData,
                borderColor: 'rgba(0, 0, 0, 0.5)',
                borderWidth: 1,
                borderDash: [5, 5],
                tension: 0,
                fill: false,
                yAxisID: "y1",
                pointRadius: 0,
                order: 3,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        parsing: false,
        normalized: true,
        plugins: {
            decimation: { enabled: true, algorithm: 'lttb', samples: 500 },
            legend: {
                position: "top",
                labels: {
                    font: { size: window.innerWidth < 600 ? 10 : 12 },
                    usePointStyle: true,
                    filter: (item) => {
                        if (item.text === "Zero Line") return false;
                        if (item.text === "Similar Dates" && similarIndices.size === 0) return false;
                        return true;
                    },
                },
            },
            title: { display: false },
            tooltip: {
                mode: "index",
                intersect: false,
                position: "nearest",
                callbacks: {
                    title: (items) => {
                        if (items.length > 0) {
                            const idx = Math.round(items[0].raw?.x ?? items[0].dataIndex);
                            return idx >= 0 && idx < labels.length ? labels[idx] : '';
                        }
                        return '';
                    },
                    label: (context) => {
                        const value = context.raw?.y ?? context.raw;
                        if (value === null || value === undefined) return null;
                        if (context.dataset.label === "S&P 500 Close") return `Close: $${value.toFixed(2)}`;
                        if (context.dataset.label === "Vol Premium Momentum") return `Vol Trend: ${value.toFixed(3)}`;
                        if (context.dataset.label === "Similar Dates") {
                            const idx = Math.round(context.raw?.x ?? context.dataIndex);
                            const row = data[idx];
                            const vt = row?.vol_trend != null ? row.vol_trend.toFixed(2) : 'n/a';
                            return `Similar (RV-IV: ${row?.rv_iv?.toFixed(0)} bps, Trend: ${vt})`;
                        }
                        return `${context.dataset.label}: ${value}`;
                    },
                    filter: (item) => {
                        if (item.dataset.label === "Zero Line") return false;
                        const val = item.raw?.y ?? item.raw;
                        return val !== null && val !== undefined;
                    },
                },
            },
            zoom: {
                pan: { enabled: true, mode: "x" },
                zoom: { wheel: { enabled: true, speed: 0.09 }, pinch: { enabled: true, speed: 0.09 }, mode: "x" },
            },
            crosshair: {
                line: { color: "rgba(0, 0, 0, 0.2)", width: 1 },
                sync: { enabled: false },
                zoom: { enabled: false },
            },
        },
        interaction: { mode: "nearest", intersect: false, axis: "x" },
        scales: {
            x: {
                type: 'linear',
                min: initialMinIdx,
                max: data.length - 1,
                title: { display: true, text: "Date" },
                ticks: {
                    autoSkip: true,
                    maxTicksLimit: 10,
                    font: { size: 11 },
                    callback: function(value) {
                        const idx = Math.round(value);
                        return idx >= 0 && idx < labels.length ? labels[idx] : '';
                    }
                },
            },
            y: {
                type: "linear",
                position: "left",
                title: { display: true, text: "S&P 500 Close ($)" },
                grid: { drawOnChartArea: true, color: "rgba(200, 200, 200, 0.2)" },
            },
            y1: {
                type: "linear",
                position: "right",
                title: { display: true, text: "\u2190 Hedging Costs Accelerating | Hedging Costs Decelerating \u2192" },
                grid: { drawOnChartArea: false },
            },
        },
    };

    const resetZoom = () => { if (chartRef.current) chartRef.current.resetZoom(); };

    return (
        <div style={{ width: '100%' }}>
            <div style={{ height: '400px' }}>
                <Line ref={chartRef} data={chartData} options={options} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', padding: '8px 0' }}>
                <span style={{ fontSize: '12px', color: '#666' }}>Updated: {timestamp}</span>
                <button onClick={resetZoom} style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: '#4a90d9', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Reset Zoom</button>
            </div>
        </div>
    );
};

export default VolTrendTimeSeriesChart;
